import { useMutation, useQueryClient } from '@tanstack/react-query';

import { State } from '@/features/single-measurement/types/state';

import {
  setFavouriteFermentation,
  updateFermentation,
} from '../../services/fermentations';
import { FermentationQueryKeys } from '../../services/query-keys';

/**
 * This hook can be use to call fermentation related endpoints
 *
 * @param onSuccessFn Any function that you need to call on success
 * @param onErrorFn Any function that you need to call on error
 * @param onSettledFn Will trigger on both success and error
 */
export const useFermentationUpdate = ({
  onSuccessFn,
  onErrorFn,
  onSettledFn,
}: {
  onSuccessFn?: () => void;
  onErrorFn?: () => void;
  onSettledFn?: () => void;
}) => {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => updateFermentation(id, { state: State.AbortedByUser }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FermentationQueryKeys.GetAllFermentations],
      });
      onSuccessFn && onSuccessFn();
    },
    onError: () => onErrorFn && onErrorFn(),
    onSettled: () => onSettledFn && onSettledFn(),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) =>
      updateFermentation(id, { state: State.UserFermentationCompleted }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FermentationQueryKeys.GetAllFermentations],
      });
      onSuccessFn && onSuccessFn();
    },
    onError: () => onErrorFn && onErrorFn(),
    onSettled: () => onSettledFn && onSettledFn(),
  });

  const setFavoriteMutation = useMutation({
    mutationFn: (opts: { id: string; isFavourite: boolean }) =>
      setFavouriteFermentation(opts.id, opts.isFavourite),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FermentationQueryKeys.GetAllFermentations],
      });
      onSuccessFn && onSuccessFn();
    },
    onError: () => onErrorFn && onErrorFn(),
    onSettled: () => onSettledFn && onSettledFn(),
  });

  return { deleteMutation, completeMutation, setFavoriteMutation };
};
